import * as React from 'react';
import { View } from 'react-native';
import Text from 'design-system/Text';
import Amount from 'design-system/Amount';
import Status from 'design-system/Status';
import CardRow from 'design-system/CardRow';
import { Button, rem, Separator, Spacer } from 'design-system';
import BlurAmountView from 'design-system/BlurAmountView';
import CardRowContainer from 'design-system/CardRowContainer';
import IconEmmaWallet from 'design-system/icons/IconEmmaWallet';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { InvestWalkthroughContext } from 'features/feed/components/walkthrough/context';
import { getTradingAccountName } from '../utils';
import { setSelectedTradingAccountId } from '../actions';
import { useAlpacaClosingInfo } from '../hooks/useAlpacaClosingInformation';
import { blockInvestOnboardingOnWeb } from '../webGate';
import { isWeb } from '../../../constants';
const ActiveTradingAccountCard = ({ image, account }) => {
    const isInInvestWalkthrough = React.useContext(InvestWalkthroughContext);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const dispatch = useAppDispatch();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const isAlpaca = account.account.broker === 'alpaca';
    const isJISATransferRequired = account.account.status === 'JISA_TRANSFER_REQUIRED';
    const AlpacaClosingInfo = useAlpacaClosingInfo();
    const ongoingProductTransferId = account.account.accountType === 'JISA' && 'ongoingProductTransferId' in account.account
        ? account.account.ongoingProductTransferId
        : undefined;
    const onPress = React.useCallback(() => {
        if (ongoingProductTransferId) {
            navigation.navigate('ProductTransferProgress', {
                productTransferId: ongoingProductTransferId,
                accountId: account.account.id,
            });
            return;
        }
        dispatch(setSelectedTradingAccountId(account.account.id));
        navigation.navigate('InvestPortfolio');
        // Transfers in stay in the mobile app, so web doesn't open the prompt
        if (!isWeb && account.account.accountType === 'ISA' && account.account.accountsHeldElsewhere?.length) {
            navigation.navigate('ProductTransferIntro', { showDismiss: true });
        }
    }, [account.account, ongoingProductTransferId]);
    const handleJISATransfer = React.useCallback(() => {
        if (blockInvestOnboardingOnWeb())
            return;
        if (isJISATransferRequired) {
            navigation.navigate('ProductTransferIntro', {
                accountId: account.account.id,
            });
        }
    }, [account.account.id, isJISATransferRequired]);
    const TotalReturn = React.useMemo(() => {
        const growthObj = account.account.growth;
        if (growthObj) {
            if (growthObj.value > 0)
                return <Amount Numbers-14 Positive value={growthObj} showSign/>;
            if (growthObj.value < 0)
                return <Amount Numbers-14 Negative value={growthObj}/>;
            return <Amount Numbers-14 value={growthObj}/>;
        }
        return null;
    }, [account.account.growth]);
    const RateOfReturn = React.useMemo(() => {
        const growthPct = account.account.growthPercent;
        if (typeof growthPct === 'number') {
            const growthPctValue = growthPct.toFixed(2);
            if (growthPct > 0) {
                return (<BlurAmountView enabled={scrambled}>
            <Text Positive Numbers-14>
              +{growthPctValue}%
            </Text>
          </BlurAmountView>);
            }
            if (growthPct < 0) {
                return (<BlurAmountView enabled={scrambled}>
            <Text Negative Numbers-14>
              {growthPctValue}%
            </Text>
          </BlurAmountView>);
            }
            return (<BlurAmountView enabled={scrambled}>
          <Text Numbers-14>{growthPctValue}%</Text>
        </BlurAmountView>);
        }
        return null;
    }, [account.account.growthPercent, scrambled]);
    const AccountValue = React.useMemo(() => {
        const value = account.account.broker === 'alpaca' ? account.account.longPositionsMarketValue : account.account.portfolioValue;
        if (value) {
            return <Amount Numbers-16 value={value}/>;
        }
        return null;
    }, [account.account.broker, account.account.longPositionsMarketValue, account.account.portfolioValue]);
    return (<TouchableBounceCard onDark noMarginBottom onPress={onPress} style={isInInvestWalkthrough ? styles.walkthroughCard : null} disabled={account.account.status !== 'ACTIVE' && !ongoingProductTransferId}>
      <View style={styles.headerContainer}>
        <IconEmmaWallet size={rem(44)}/>
        <Spacer w={16}/>
        <View style={styles.flex}>
          {ongoingProductTransferId ||
            account.account.status === 'IDENTITY_CHECKS_IN_PROGRESS' ||
            account.account.status === 'OPENING_IN_PROGRESS' ? (<Status active={false} activeText="Working" inActiveColor={colors.status.informational} inactiveText={ongoingProductTransferId ? 'Transfer pending' : 'Setting up'}/>) : null}
          <View style={styles.row}>
            <View style={styles.titleContainer}>
              <Text Text-16 numberOfLines={2}>
                {getTradingAccountName(account.account)}
              </Text>
              {image ? (<>
                  <Spacer h={4}/>
                  {image}
                </>) : null}
            </View>
            {!isJISATransferRequired && <View style={styles.valueContainer}>{AccountValue}</View>}
          </View>
        </View>
      </View>
      {!!isJISATransferRequired && !ongoingProductTransferId && (<View style={styles.btnContainer}>
          <Button brandReversed square onPress={handleJISATransfer} title="Transfer a JISA"/>
        </View>)}
      <Separator />
      {!!isAlpaca && !!AlpacaClosingInfo && <View style={styles.closingInfoContainer}>{AlpacaClosingInfo}</View>}
      {account.account.status === 'ACTIVE' && (<CardRowContainer>
          <CardRow small tableSpacing title="Total return" info={TotalReturn}/>
          <CardRow small tableSpacing title="Rate of return" info={RateOfReturn}/>
        </CardRowContainer>)}
    </TouchableBounceCard>);
};
export default React.memo(ActiveTradingAccountCard);
const styleSet = createStyleSheets(() => ({
    headerContainer: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    row: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    titleContainer: {
        flex: 1,
    },
    valueContainer: {
        marginLeft: rem(16),
    },
    closingInfoContainer: {
        paddingTop: rem(16),
        paddingBottom: rem(8),
        paddingHorizontal: rem(16),
    },
    walkthroughCard: {
        marginHorizontal: rem(4),
    },
    btnContainer: {
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
    },
}));
